import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { workshopDashboardService } from '@/app/services/dashboardService';
import { JobCardListItem } from '@/app/types/jobCard';
import { StatusBadge } from '@/app/pages/workshop/components/PriorityBadge';
import { History, Inbox, ChevronRight } from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';

interface RecentItemProps {
  item: JobCardListItem;
  onClick: () => void;
}

const RecentItem: React.FC<RecentItemProps> = ({ item, onClick }) => {
  const vehicleDisplay = [item.vehicle.make, item.vehicle.model].filter(Boolean).join(' ');

  // Calculate relative time
  const updatedAt = item.created_at; // Use created_at as proxy since updated_at may not be in list item
  let timeAgo = '';
  try {
    timeAgo = formatDistanceToNow(parseISO(updatedAt), { addSuffix: true });
  } catch {
    timeAgo = '';
  }

  return (
    <div
      className="flex items-center justify-between p-3 hover:bg-muted/50 cursor-pointer transition-colors group"
      onClick={onClick}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">
              {item.vehicle.registration_number}
            </span>
            {vehicleDisplay && (
              <span className="text-xs text-muted-foreground hidden sm:inline">
                {vehicleDisplay}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-muted-foreground">
              {item.document_number}
            </span>
            {timeAgo && (
              <span className="text-xs text-muted-foreground">
                • {timeAgo}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0 ml-2">
        <StatusBadge status={item.status} className="text-xs" />
        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    </div>
  );
};

const EmptyState: React.FC = () => (
  <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
    <Inbox className="h-8 w-8 mb-2 opacity-50" />
    <p className="text-sm">No recent job cards</p>
  </div>
);

const ItemSkeleton: React.FC = () => (
  <div className="flex items-center justify-between p-3">
    <div className="flex items-center gap-3 flex-1">
      <div className="space-y-2 flex-1">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-3 w-36" />
      </div>
    </div>
    <Skeleton className="h-5 w-20" />
  </div>
);

const RecentJobCards: React.FC = () => {
  const navigate = useNavigate();

  const { data: recentCards, isLoading } = useQuery({
    queryKey: ['workshopDashboard', 'recentJobCards'],
    queryFn: () => workshopDashboardService.getRecentJobCards(10),
    refetchInterval: 30000, // Refresh every 30 seconds
    staleTime: 15000,
  });

  const handleItemClick = (id: number) => {
    navigate(`/workshop/job-cards/detail/${id}`);
  };

  const handleViewAll = () => {
    navigate('/workshop/job-cards/list');
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <History className="h-4 w-4 text-slate-500" />
            Recent Job Cards
          </CardTitle>
          <button
            onClick={handleViewAll}
            className="text-xs text-primary hover:underline"
          >
            View all
          </button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading ? (
          <div className="divide-y">
            {[1, 2, 3, 4, 5].map((i) => (
              <ItemSkeleton key={i} />
            ))}
          </div>
        ) : !recentCards || recentCards.length === 0 ? (
          <EmptyState />
        ) : (
          <ScrollArea className="h-[300px]">
            <div className="divide-y">
              {recentCards.map((item) => (
                <RecentItem
                  key={item.id}
                  item={item}
                  onClick={() => handleItemClick(item.id)}
                />
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};

export default RecentJobCards;
